import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { Button, EmptyState } from '@immo/ui-kit';
import { StaleClientError } from '@immo/api-contract';
import { OutdatedView } from '@/components/errors/outdated-view';
import { useTranslations } from '@/i18n/use-translations';
import { setAccessToken } from '@/lib/auth/access-token-store';
import { captureFrontendError } from '@/lib/errors/error-context';
import { FrontendError } from '@/lib/errors/frontend-error';
import { NetworkError } from '@/lib/errors/network-error';
import { NotFoundPage } from './not-found';

/**
 * Route-level `errorElement` — rendered by React Router whenever a route's
 * loader, action, or element throws. Per ADR 0014 (amended 2026-05-08) this
 * is the SPA equivalent of App Router's per-segment `error.tsx`.
 *
 * 401 / 403 are auth-flow signals (the API hook maps domain `AuthError` /
 * `AccessDeniedError` to those statuses): we clear the in-memory access
 * token and redirect to the sign-in or access-denied page instead of
 * surfacing them to Sentry. 404 falls through to `NotFoundPage`. Everything
 * else is a real error and gets captured with a meaningful synthesised
 * message — without this, `String(errorResponse)` would stringify to
 * `"[object Object]"` and bury the signal.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const t = useTranslations('pages.error');
  const tRoot = useTranslations();

  const authRedirect = authRedirectFor(error);

  useEffect(() => {
    if (authRedirect !== null) {
      setAccessToken(null);
      // A 403 from a route the operator simply may not read is a different
      // message from "you are not platform staff" — the reason travels as
      // navigation state so it can't be forged from the address bar.
      navigate(authRedirect, {
        replace: true,
        ...(authRedirect === '/access-denied' ? { state: { reason: 'missing-permission' } } : {}),
      });

      return;
    }

    if (isRouteErrorResponse(error) && error.status === 404) {
      return;
    }

    captureFrontendError(
      error instanceof NetworkError ? new FrontendError({ code: error.code, cause: error }) : toError(error),
      'route-error-element',
    );
  }, [authRedirect, error, navigate]);

  if (authRedirect !== null) {
    return null;
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  // Every operator page loads its data from a loader, so this — not the root
  // boundary — is where a stale bundle's parse failure actually lands.
  if (error instanceof StaleClientError) {
    return <OutdatedView />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <EmptyState
        title={t('title')}
        description={error instanceof NetworkError ? tRoot(error.code) : t('body')}
        action={<Button onClick={() => navigate(0)}>{t('try-again')}</Button>}
      />
    </main>
  );
}

function authRedirectFor(error: unknown): string | null {
  if (!isRouteErrorResponse(error)) return null;

  if (error.status === 401) return '/sign-in';

  if (error.status === 403) return '/access-denied';

  return null;
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;

  if (isRouteErrorResponse(error)) {
    const detail = error.statusText !== '' ? error.statusText : safeStringify(error.data);

    return new Error(`Route ${error.status}: ${detail}`);
  }

  return new Error(String(error));
}

function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
